/**
 * Query Volume Chart Component
 *
 * Area chart showing successful queries and errors over time
 */

import { useId, useMemo, useCallback } from 'react';
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart';
import type { LegendPayload } from 'recharts/types/component/DefaultLegendContent';
import { formatChartTime, formatCompactNumber } from '@/lib/formatters';
import { ChartCard, CHART_HEIGHT_CLASSES } from './ChartCard';
import {
  createDotProps,
  createActiveDotProps,
  createGradientIds,
} from './chart-utils';
import { MetricTooltip } from './MetricTooltip';
import { METRIC_DESCRIPTIONS } from './MetricDescriptions';
import type { DataPoint } from '@/types/dashboard';

interface QueryVolumeChartProps {
  queryData: DataPoint[] | undefined;
  errorData: DataPoint[] | undefined;
  isLoading: boolean;
  totalQueries?: number;
  totalErrors?: number;
}

interface MergedVolumePoint {
  time: string;
  timestamp: string; // Original ISO timestamp for sorting
  successful: number;
  errors: number;
}

function mergeVolumeData(
  queryData: DataPoint[] | undefined,
  errorData: DataPoint[] | undefined,
): MergedVolumePoint[] {
  const dataMap = new Map<string, MergedVolumePoint>();

  // Add query data (total queries)
  // Use original timestamp as key to properly merge data points
  queryData?.forEach((point) => {
    dataMap.set(point.timestamp, {
      time: formatChartTime(point.timestamp),
      timestamp: point.timestamp,
      successful: Math.round(point.value),
      errors: 0,
    });
  });

  // Merge error data and adjust successful count
  errorData?.forEach((point) => {
    const errors = Math.round(point.value);
    const existing = dataMap.get(point.timestamp);
    if (existing) {
      existing.errors = errors;
      // Successful = total queries - errors
      existing.successful = Math.max(0, existing.successful - errors);
    } else {
      dataMap.set(point.timestamp, {
        time: formatChartTime(point.timestamp),
        timestamp: point.timestamp,
        successful: 0,
        errors,
      });
    }
  });

  // Sort chronologically by original timestamp
  return Array.from(dataMap.values()).sort((a, b) =>
    a.timestamp.localeCompare(b.timestamp),
  );
}

const chartConfig = {
  successful: {
    label: 'Successes',
    color: 'hsl(var(--chart-success))',
  },
  errors: {
    label: 'Errors',
    color: 'hsl(var(--chart-error))',
  },
} satisfies ChartConfig;

export function QueryVolumeChart({
  queryData,
  errorData,
  isLoading,
  totalQueries,
  totalErrors,
}: QueryVolumeChartProps) {
  // Generate unique gradient IDs for this chart instance
  const chartId = useId();
  const gradientIds = useMemo(
    () => createGradientIds(chartId, ['fillErrors', 'fillSuccessful']),
    [chartId],
  );

  // Memoize data transformation to avoid recalculating on every render
  const chartData = useMemo(
    () => mergeVolumeData(queryData, errorData),
    [queryData, errorData],
  );

  // Memoize legend reordering callback
  const renderLegend = useCallback(
    (props: { payload?: readonly LegendPayload[] }) => {
      const reorderedPayload = props.payload
        ? [...props.payload].sort((a, b) => {
            const order = ['successful', 'errors'];
            return (
              order.indexOf(String(a.dataKey)) -
              order.indexOf(String(b.dataKey))
            );
          })
        : [];
      return <ChartLegendContent {...props} payload={reorderedPayload} />;
    },
    [],
  );

  // Show skeleton when loading OR when data hasn't arrived yet (props are undefined)
  const showSkeleton =
    isLoading || (queryData === undefined && errorData === undefined);

  // Show "no data" only when data has loaded but is genuinely empty
  const isEmpty = !showSkeleton && chartData.length === 0;

  // Header summary content with tooltips for metric explanations
  const headerContent = totalQueries !== undefined && (
    <div className="flex gap-4 text-sm text-muted-foreground">
      <MetricTooltip
        label="Total"
        value={formatCompactNumber(totalQueries)}
        tooltip={METRIC_DESCRIPTIONS.totalQueries}
      />
      {totalErrors !== undefined && totalErrors > 0 && (
        <span className="text-destructive">
          <MetricTooltip
            label="Errors"
            value={formatCompactNumber(totalErrors)}
            tooltip={METRIC_DESCRIPTIONS.totalErrors}
          />
        </span>
      )}
    </div>
  );

  return (
    <ChartCard
      title="Query Volume"
      tooltip={METRIC_DESCRIPTIONS.queryVolume}
      headerContent={headerContent}
      isLoading={showSkeleton}
      isEmpty={isEmpty}
      emptyAriaLabel="No query volume data available"
    >
      <ChartContainer
        config={chartConfig}
        className={`${CHART_HEIGHT_CLASSES} w-full`}
      >
        <AreaChart data={chartData} accessibilityLayer>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="time"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
            allowDecimals={false}
            domain={[0, (dataMax: number) => Math.max(dataMax, 1)]}
          />
          <ChartTooltip content={<ChartTooltipContent />} />
          <ChartLegend content={renderLegend} />
          <defs>
            <linearGradient
              id={gradientIds.fillErrors}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="5%"
                stopColor="var(--color-errors)"
                stopOpacity={0.8}
              />
              <stop
                offset="95%"
                stopColor="var(--color-errors)"
                stopOpacity={0.1}
              />
            </linearGradient>
            <linearGradient
              id={gradientIds.fillSuccessful}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
            >
              <stop
                offset="5%"
                stopColor="var(--color-successful)"
                stopOpacity={0.8}
              />
              <stop
                offset="95%"
                stopColor="var(--color-successful)"
                stopOpacity={0.1}
              />
            </linearGradient>
          </defs>
          <Area
            dataKey="errors"
            name="Errors"
            type="monotone"
            fill={`url(#${gradientIds.fillErrors})`}
            fillOpacity={0.4}
            stroke="var(--color-errors)"
            strokeWidth={2}
            connectNulls
            dot={createDotProps(chartData.length, 'var(--color-errors)')}
            activeDot={createActiveDotProps(
              chartData.length,
              'var(--color-errors)',
            )}
          />
          <Area
            dataKey="successful"
            name="Successes"
            type="monotone"
            fill={`url(#${gradientIds.fillSuccessful})`}
            fillOpacity={0.4}
            stroke="var(--color-successful)"
            strokeWidth={2}
            connectNulls
            dot={createDotProps(chartData.length, 'var(--color-successful)')}
            activeDot={createActiveDotProps(
              chartData.length,
              'var(--color-successful)',
            )}
          />
        </AreaChart>
      </ChartContainer>
    </ChartCard>
  );
}
